import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Button, CopyButton, Field, Select } from '@evogard/ui';
import type { AsaasOverview } from '@evogard/contract';
import {
  ASAAS_ENVIRONMENT_LABELS,
  ASAAS_ENVIRONMENTS,
  type AsaasEnvironment,
} from '@evogard/domain';
import { api, ok, errorMessage } from '../../lib/api.js';

const format = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});

/**
 * O resto do Asaas na tela de integrações (task 1465714350, 15/10/2026): o
 * ambiente e o webhook. A chave é a credencial da integração `gateway`, colada
 * como as outras.
 *
 * Desde 16/10/2026 são duas peças e não um card: o ambiente mora na aba
 * "Conexão", ao lado da chave — uma não vale sem a outra —, e o webhook tem a
 * aba dele. As duas leem a mesma consulta (`useAsaasOverview`), e a tela usa a
 * mesma para dizer na lista se falta o webhook.
 *
 * O webhook é registrado POR ESTA TELA: a casa gera o token, manda ao Asaas e
 * o guarda cifrado. Quem configura não copia nada do painel do Asaas.
 */
export function asaasKey(tenantId: number | undefined) {
  return ['plataforma', 'integracoes', 'asaas', tenantId];
}

export function useAsaasOverview(tenantId: number | undefined) {
  return useQuery({
    queryKey: asaasKey(tenantId),
    queryFn: async () => {
      const r = await api.asaas.getAsaas();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar o Asaas.'));
      }
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

/** O ambiente do Asaas desta base. A troca apaga o webhook registrado. */
export function AsaasEnvironmentSection({
  tenantId,
  canEdit,
}: {
  tenantId: number | undefined;
  canEdit: boolean;
}) {
  const client = useQueryClient();
  const query = useAsaasOverview(tenantId);
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const saveEnvironment = useMutation({
    mutationFn: async (environment: AsaasEnvironment) =>
      ok(
        await api.asaas.saveAsaasSettings({ body: { environment } }),
        200,
        'Não foi possível trocar o ambiente.',
      ),
    onSuccess: (overview) => {
      client.setQueryData(asaasKey(tenantId), overview);
      setActionError(null);
      setNote(
        `Ambiente trocado para ${ASAAS_ENVIRONMENT_LABELS[overview.environment]}. Cole a chave deste ambiente e registre o webhook de novo.`,
      );
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const data = query.data;

  return (
    <section className="flex flex-col gap-3" aria-labelledby="asaas-ambiente">
      <h3 id="asaas-ambiente" className="text-sm font-semibold text-texto">
        Ambiente
      </h3>
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {actionError && <Alert tone="erro">{actionError}</Alert>}
      {note && <Alert tone="sucesso">{note}</Alert>}
      {data && (
        <div className="max-w-xs">
          <Select
            label="Ambiente do Asaas"
            hiddenLabel
            value={data.environment}
            disabled={!canEdit || saveEnvironment.isPending}
            onChange={(e) => {
              setNote(null);
              saveEnvironment.mutate(e.target.value as AsaasEnvironment);
            }}
            help={
              data.productionAllowed
                ? 'A chave de um ambiente não vale no outro.'
                : 'Fora de produção a API usa só o Sandbox: o desenvolvimento nunca cobra um associado de verdade.'
            }
          >
            {ASAAS_ENVIRONMENTS.map((env) => (
              <option
                key={env}
                value={env}
                disabled={env === 'producao' && !data.productionAllowed}
              >
                {ASAAS_ENVIRONMENT_LABELS[env]}
              </option>
            ))}
          </Select>
        </div>
      )}
    </section>
  );
}

/** O webhook do Asaas desta base: o estado lido do Asaas, o endereço e o registro. */
export function AsaasWebhookPanel({
  tenantId,
  canEdit,
}: {
  tenantId: number | undefined;
  canEdit: boolean;
}) {
  const client = useQueryClient();
  const query = useAsaasOverview(tenantId);
  const statusKey = ['plataforma', 'integracoes', 'asaas', 'webhook', tenantId];
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const webhookStatus = useQuery({
    queryKey: statusKey,
    queryFn: async () =>
      ok(await api.asaas.getAsaasWebhookStatus(), 200, 'Não foi possível ler o webhook.'),
    enabled: tenantId !== undefined && Boolean(query.data?.webhook.registered),
  });

  return (
    <div className="flex flex-col gap-4">
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {actionError && <Alert tone="erro">{actionError}</Alert>}
      {note && <Alert tone="sucesso">{note}</Alert>}
      {query.data && (
        <WebhookSection
          overview={query.data}
          canEdit={canEdit}
          status={webhookStatus.data ?? null}
          statusLoading={webhookStatus.isLoading}
          onSaved={(overview) => {
            client.setQueryData(asaasKey(tenantId), overview);
            void client.invalidateQueries({ queryKey: statusKey });
            setActionError(null);
            setNote('Webhook registrado no Asaas. O token novo já está valendo.');
          }}
          onError={(message) => {
            setNote(null);
            setActionError(message);
          }}
        />
      )}
    </div>
  );
}

function WebhookSection({
  overview,
  canEdit,
  status,
  statusLoading,
  onSaved,
  onError,
}: {
  overview: AsaasOverview;
  canEdit: boolean;
  status: {
    available: boolean;
    detail: string | null;
    enabled: boolean | null;
    interrupted: boolean | null;
  } | null;
  statusLoading: boolean;
  onSaved: (overview: AsaasOverview) => void;
  onError: (message: string) => void;
}) {
  const { webhook, expectedWebhookUrl } = overview;
  const [email, setEmail] = useState(webhook.email ?? '');
  const [submitted, setSubmitted] = useState(false);
  const emailProblem = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
    ? null
    : 'Informe o e-mail que recebe os avisos do Asaas sobre o webhook.';

  const register = useMutation({
    mutationFn: async () =>
      ok(
        await api.asaas.registerAsaasWebhook({ body: { email: email.trim() } }),
        200,
        'Não foi possível registrar o webhook.',
      ),
    onSuccess: (o) => {
      setSubmitted(false);
      onSaved(o);
    },
    onError: (e: Error) => onError(e.message),
  });

  const urlChanged = webhook.registered && webhook.url !== expectedWebhookUrl;
  const local = /\/\/(localhost|127\.0\.0\.1)[:/]/.test(expectedWebhookUrl);

  return (
    // o título é a aba: repetir "Webhook" aqui seria um segundo rótulo igual
    <section className="flex flex-col gap-3" aria-label="Webhook do Asaas">
      <div className="flex flex-wrap items-center gap-2">
        {!webhook.registered ? (
          <Badge tone="alerta">Não registrado</Badge>
        ) : statusLoading ? (
          <Badge tone="neutro">Consultando o Asaas…</Badge>
        ) : status?.available ? (
          <>
            <Badge tone={status.enabled ? 'sucesso' : 'alerta'}>
              {status.enabled ? 'Ativo' : 'Desativado'}
            </Badge>
            <Badge tone={status.interrupted ? 'erro' : 'sucesso'}>
              {status.interrupted ? 'Fila interrompida' : 'Fila entregando'}
            </Badge>
            <Badge tone="neutro">Envio sequencial</Badge>
          </>
        ) : (
          <Badge tone="alerta">Estado desconhecido</Badge>
        )}
        {webhook.registeredAt && (
          <span className="text-sm text-texto-suave">
            Registrado em {format.format(new Date(webhook.registeredAt))}
          </span>
        )}
      </div>

      {webhook.registered && status && !status.available && status.detail && (
        <Alert tone="alerta">{status.detail}</Alert>
      )}
      {status?.interrupted && (
        <Alert tone="erro" title="O Asaas parou de entregar os avisos desta base.">
          Depois de 15 falhas seguidas ele interrompe a fila. Enquanto ela estiver parada,
          o pagamento só aparece aqui pela conciliação, de 15 em 15 minutos. Corrija a
          causa e registre o webhook de novo: o registro retoma a fila.
        </Alert>
      )}
      {urlChanged && (
        <Alert tone="alerta" title="O endereço registrado não é o desta API.">
          Registre de novo para o Asaas passar a avisar em {expectedWebhookUrl}.
        </Alert>
      )}

      <dl className="grid gap-1 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
        <dt className="text-texto-suave">Endereço</dt>
        <dd className="flex min-w-0 items-center gap-2">
          <code className="font-mono text-sm break-all text-texto">
            {expectedWebhookUrl}
          </code>
          <CopyButton text={expectedWebhookUrl} label="Copiar o endereço do webhook" />
        </dd>
        {webhook.email && (
          <>
            <dt className="text-texto-suave">Avisos para</dt>
            <dd>{webhook.email}</dd>
          </>
        )}
      </dl>
      {local && (
        <p className="text-sm text-texto-suave">
          Este endereço é local: o Asaas não alcança esta máquina, e recusa o registro. Em
          desenvolvimento, teste o webhook por um túnel apontado para a API.
        </p>
      )}

      {canEdit && (
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-start"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (!emailProblem) register.mutate();
          }}
        >
          <div className="min-w-0 flex-1">
            <Field
              label="E-mail de avisos"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
              error={submitted ? emailProblem : null}
              help="O Asaas escreve para este endereço quando a fila do webhook tem problema."
            />
          </div>
          <div className="sm:pt-7">
            <Button type="submit" loading={register.isPending}>
              {webhook.registered ? 'Registrar de novo' : 'Registrar no Asaas'}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
